import PropTypes from 'prop-types';

/**
 * Read-only Validation Rules block for the view-mode field expansion.
 * Lists only the constraints that carry a value. Returns null when the
 * field has no constraints to show.
 */

const FORMAT_LABELS = {
	email: 'email',
	uri: 'URI',
	uuid: 'UUID',
	date: 'date',
	'date-time': 'date-time',
};

function isMeaningful(value) {
	if (value == null || value === '') return false;
	if (Array.isArray(value)) return value.length > 0;
	if (typeof value === 'string') return value.trim() !== '';
	return true;
}

function joinEnum(value) {
	if (Array.isArray(value)) return value.join(', ');
	if (typeof value === 'string') return value;
	return String(value);
}

function collectRows(field) {
	const rows = [];
	if (isMeaningful(field.minLength)) rows.push(['Min length', field.minLength]);
	if (isMeaningful(field.maxLength)) rows.push(['Max length', field.maxLength]);
	if (isMeaningful(field.minimum)) rows.push(['Minimum', field.minimum]);
	if (isMeaningful(field.maximum)) rows.push(['Maximum', field.maximum]);
	if (isMeaningful(field.exclusiveMinimum)) rows.push(['Exclusive minimum', `> ${field.exclusiveMinimum}`]);
	if (isMeaningful(field.exclusiveMaximum)) rows.push(['Exclusive maximum', `< ${field.exclusiveMaximum}`]);
	if (isMeaningful(field.multipleOf)) rows.push(['Multiple of', field.multipleOf]);
	if (isMeaningful(field.minItems)) rows.push(['Min items', field.minItems]);
	if (isMeaningful(field.maxItems)) rows.push(['Max items', field.maxItems]);
	if (field.uniqueItems === true) rows.push(['Unique items', 'Required']);
	if (isMeaningful(field.pattern)) rows.push(['Pattern', field.pattern]);
	if (isMeaningful(field.format)) rows.push(['Format', FORMAT_LABELS[field.format] ?? field.format]);
	if (isMeaningful(field.enumValues)) rows.push(['Allowed values', joinEnum(field.enumValues)]);
	return rows;
}

export default function ValidationRulesView({ field, fieldId }) {
	const rows = collectRows(field);
	if (rows.length === 0) return null;

	return (
		<section
			className="dsv-view__section dsv-view__section--validation"
			aria-label="Validation Rules"
			data-testid={`dsv-view-validation-${fieldId}`}
			style={sectionStyle('#16a34a')}
		>
			<header style={sectionHeaderStyle}>Validation Rules</header>
			<dl style={dlStyle}>
				{rows.map(([label, value]) => (
					<div key={label} style={rowStyle}>
						<dt style={dtStyle}>{label}</dt>
						<dd style={ddStyle}>{String(value)}</dd>
					</div>
				))}
			</dl>
		</section>
	);
}

ValidationRulesView.propTypes = {
	field: PropTypes.object.isRequired,
	fieldId: PropTypes.string,
};

function sectionStyle(accent) {
	return {
		borderLeft: `3px solid ${accent}`,
		background: '#f8fafc',
		padding: '10px 14px',
		margin: '8px 0',
		borderRadius: '0 4px 4px 0',
	};
}
const sectionHeaderStyle = {
	fontSize: '11px',
	fontWeight: 600,
	textTransform: 'uppercase',
	letterSpacing: '0.04em',
	color: '#374151',
	marginBottom: '6px',
};
const dlStyle = { display: 'grid', gridTemplateColumns: '160px 1fr', gap: '4px 14px', margin: 0 };
const rowStyle = { display: 'contents' };
const dtStyle = { color: '#6b7280', fontSize: '13px' };
const ddStyle = { margin: 0, fontSize: '13px', color: '#111827', wordBreak: 'break-word', fontFamily: 'inherit' };
